'use client'

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import type { ClientConfig } from '@/config/schema'
import { initializeClientEnv, type ClientEnv, type ClientEnvServiceFactories } from '@/env/client'

interface ClientEnvContextValue {
    config: ClientConfig
    env: ClientEnv | null
}

const ClientEnvContext = createContext<ClientEnvContextValue | null>(null)

export interface ClientEnvProviderProps {
    config: ClientConfig
    /** Optional service factories for dependency injection (testing or swapping implementations) */
    factories?: ClientEnvServiceFactories
    children: ReactNode
}

/**
 * Provider component that initializes and provides ClientEnv to child components.
 *
 * Config is available immediately (works during SSR).
 * Services (logger, Datadog) are initialized in useEffect (client-only) to avoid
 * SSR issues with browser-only dependencies.
 *
 * This provider makes ClientEnv available both via:
 * - React hook: useClientEnv() - for components within the provider tree
 * - Direct access: getClientEnv() - for code outside React (e.g., global error boundary)
 *
 * Should be placed in the root layout.
 *
 * @param config - Client configuration
 * @param factories - Optional custom factories for creating services
 */
export function ClientEnvProvider({ config, factories, children }: ClientEnvProviderProps) {
    const [clientEnv, setClientEnv] = useState<ClientEnv | null>(null)

    useEffect(() => {
        setClientEnv(initializeClientEnv(config, factories))
    }, [config, factories])

    const contextValue: ClientEnvContextValue = { config, env: clientEnv }

    return <ClientEnvContext.Provider value={contextValue}>{children}</ClientEnvContext.Provider>
}

/**
 * Hook to access the ClientEnv from client components.
 *
 * Returns null during SSR and initial hydration (before useEffect runs).
 * Components should handle the null case for any render-time usage.
 * For event handlers and effects, the env will be available.
 *
 * @returns ClientEnv or null if not yet initialized
 */
export function useClientEnv(): ClientEnv | null {
    const context = useContext(ClientEnvContext)
    return context?.env ?? null
}

/**
 * Hook to access client config. Available during SSR unlike useClientEnv().
 *
 * @throws Error if used outside ClientEnvProvider
 */
export function useClientConfig(): ClientConfig {
    const context = useContext(ClientEnvContext)
    if (!context) {
        throw new Error('useClientConfig must be used within a ClientEnvProvider')
    }
    return context.config
}
